import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { msalConfig } from './msalConfig';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MainLayout from './components/layout/MainLayout'; // Importar MainLayout
import { StoreProvider, useStore } from './store/useStore';

// Crear la instancia de MSAL
const msalInstance = new PublicClientApplication({
  auth: {
    ...msalConfig.auth,
    redirectUri: window.location.origin,
    navigateToLoginRequestUrl: true
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: true
  }
});

// Variable para rastrear si MSAL está inicializado
let msalInitialized = false;

// Inicializar MSAL al cargar el módulo
try {
  msalInstance.initialize().catch(error => {
    console.error('Error al inicializar MSAL:', error);
  });
} catch (error) {
  console.error('Error al inicializar MSAL:', error);
}

// Componente para manejar la inicialización de MSAL
const MsalInitializer = ({ children }) => {
  const { instance } = useMsal();
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const initialize = async () => {
      try {
        // Procesar la respuesta de redirección si existe
        const response = await instance.handleRedirectPromise();
        
        if (response) {
          instance.setActiveAccount(response.account);
        } else {
          // Si no hay respuesta de redirección, verificar si hay una cuenta activa
          const accounts = instance.getAllAccounts();
          if (accounts.length > 0) {
            instance.setActiveAccount(accounts[0]);
          }
        }
        
        if (isMounted) {
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Error en el componente MsalInitializer:', error);
        if (isMounted) {
          setError('Error al inicializar la autenticación');
          setIsInitialized(true); // Continuar para mostrar la interfaz de error
        }
      }
    };

    // Inicializar MSAL
    initialize();


    // Limpiar al desmontar
    return () => {
      isMounted = false;
    };
  }, [instance]);

  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center p-6 max-w-md mx-auto bg-white dark:bg-gray-800 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold text-red-600 dark:text-red-400 mb-4">Error de autenticación</h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return children;
};

// Componente para manejar el tema
const ThemeWrapper = ({ children }) => {
  const darkMode = useStore((state) => state.darkMode);
  
  useEffect(() => {
    // Aplicar el tema al body
    if (darkMode) {
      document.body.classList.add('dark');
      document.body.classList.remove('light');
    } else {
      document.body.classList.add('light');
      document.body.classList.remove('dark');
    }
  }, [darkMode]);

  return children;
};

// Componente principal de la aplicación
const AppContent = () => {
  return (
    <Router>
      {/* El div flex h-screen se moverá a MainLayout o ya no será necesario aquí si MainLayout lo maneja */}
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<MainLayout />}> {/* Ruta contenedora para el layout principal */}
          <Route path="/dashboard" element={<Dashboard />} />
          {/* Otras rutas que usen MainLayout pueden ir aquí */}
        </Route>
        <Route path="/" element={<Navigate to="/login" replace />} /> {/* Redirigir la raíz a login */}
        {/* Considerar si la ruta raíz '/' debería ir a '/dashboard' si el usuario ya está autenticado */}
        {/* Esto usualmente se maneja con un componente ProtectedRoute o lógica dentro de la ruta '/' */}
        <Route path="*" element={<Navigate to="/" replace />} /> {/* Catch-all */}
      </Routes>
    </Router>
  );
};

// Componente raíz de la aplicación
function App() {
  return (
    <StoreProvider>
      <MsalProvider instance={msalInstance}>
        <MsalInitializer>
          <ThemeWrapper>
            <AppContent />
          </ThemeWrapper>
        </MsalInitializer>
      </MsalProvider>
    </StoreProvider>
  );
}

export default App;
